import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Quote, Star, Building2, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { optimizedImageUrl } from "@/lib/optimized-image";
import type { Testimonial } from "@shared/schema";

interface TestimonialDisplayProps {
  pageSlug: string;
  sectionId?: string;
  className?: string;
  overrideStyle?: "card" | "carousel" | "grid" | "featured" | "minimal" | "hero";
  maxCount?: number;
}

export function TestimonialDisplay({ 
  pageSlug, 
  sectionId,
  className,
  overrideStyle,
  maxCount
}: TestimonialDisplayProps) {
  const { data: result, isLoading } = useQuery<{ testimonials: Testimonial[]; displayStyle: string }>({
    queryKey: ["/api/testimonials", { page: pageSlug, section: sectionId }],
    queryFn: async () => {
      const params = new URLSearchParams({ page: pageSlug });
      if (sectionId) params.set("section", sectionId);
      const res = await fetch(`/api/testimonials?${params}`);
      if (!res.ok) throw new Error("Failed to fetch testimonials");
      return res.json();
    },
  });

  if (isLoading) {
    return <TestimonialSkeleton />;
  }

  if (!result?.testimonials?.length) {
    return null;
  }

  const testimonials = maxCount ? result.testimonials.slice(0, maxCount) : result.testimonials;
  const style = overrideStyle || result.displayStyle || "card";

  switch (style) {
    case "carousel":
      return <TestimonialCarousel testimonials={testimonials} className={className} />;
    case "grid":
      return <TestimonialGrid testimonials={testimonials} className={className} />;
    case "featured":
      return <TestimonialFeatured testimonials={testimonials} className={className} />;
    case "minimal":
      return <TestimonialMinimal testimonials={testimonials} className={className} />;
    case "hero":
      return <TestimonialHero testimonials={testimonials} className={className} />;
    default:
      return <TestimonialCards testimonials={testimonials} className={className} />;
  }
}

function TestimonialSkeleton() {
  return (
    <div className="flex gap-4">
      {[1, 2, 3].map(i => (
        <Card key={i} className="flex-1">
          <CardContent className="p-6">
            <div className="flex gap-3 mb-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <Skeleton className="h-20 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TestimonialCards({ testimonials, className }: { testimonials: Testimonial[]; className?: string }) {
  return (
    <div className={cn("grid gap-6 md:grid-cols-2 lg:grid-cols-3 overflow-hidden", className)}>
      {testimonials.map(testimonial => (
        <TestimonialCard key={testimonial.id} testimonial={testimonial} />
      ))}
    </div>
  );
}

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  const initials = testimonial.name.split(" ").map(n => n[0]).join("").slice(0, 2);
  const quote = testimonial.shortQuote || testimonial.fullQuote;
  
  return (
    <Card className="h-full overflow-hidden" data-testid={`testimonial-card-${testimonial.id}`}>
      <CardContent className="p-5 sm:p-6 flex flex-col h-full min-w-0">
        <div className="flex items-start gap-3 mb-4 min-w-0">
          <Avatar className="h-12 w-12 border-2 border-primary/10 flex-shrink-0">
            <AvatarImage src={optimizedImageUrl(testimonial.profilePicture, 96) || undefined} alt={testimonial.name} />
            <AvatarFallback className="bg-primary/5 text-primary font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <p className="font-semibold truncate">{testimonial.name}</p>
              {testimonial.isFeatured && (
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
              )}
            </div>
            {(testimonial.title || testimonial.company) && (
              <p className="text-sm text-muted-foreground truncate">
                {testimonial.title}
                {testimonial.title && testimonial.company && " at "}
                {testimonial.company}
              </p>
            )}
          </div>
        </div>
        <blockquote className="flex-1 text-sm text-muted-foreground italic relative pl-4 border-l-2 border-primary/20 break-words overflow-hidden">
          <Quote className="absolute -left-1 -top-1 h-4 w-4 text-primary/30" />
          "{quote.length > 200 ? quote.slice(0, 200) + "..." : quote}"
        </blockquote>
        {testimonial.companyLogo && (
          <div className="mt-4 pt-4 border-t">
            <img 
              src={testimonial.companyLogo} 
              alt={testimonial.company || "Company"} 
              className="h-6 object-contain opacity-60"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TestimonialCarousel({ testimonials, className }: { testimonials: Testimonial[]; className?: string }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const next = useCallback(() => {
    setCurrentIndex(i => (i + 1) % testimonials.length);
  }, [testimonials.length]);

  const prev = useCallback(() => {
    setCurrentIndex(i => (i - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, next]);

  const testimonial = testimonials[currentIndex];
  const quote = testimonial.shortQuote || testimonial.fullQuote;
  const initials = testimonial.name.split(" ").map(n => n[0]).join("").slice(0, 2);

  return (
    <div 
      className={cn("relative", className)}
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
      data-testid="testimonial-carousel"
    >
      <Card className="overflow-hidden">
        <CardContent className="p-5 sm:p-8 md:p-12">
          <div className="flex flex-col items-center text-center max-w-3xl mx-auto min-w-0">
            <Quote className="h-10 w-10 text-primary/20 mb-6" />
            <blockquote className="text-lg md:text-xl text-foreground mb-8 italic break-words w-full">
              "{quote}"
            </blockquote>
            <Avatar className="h-16 w-16 mb-4 border-2 border-primary/20">
              <AvatarImage src={optimizedImageUrl(testimonial.profilePicture, 128) || undefined} alt={testimonial.name} />
              <AvatarFallback className="bg-primary/5 text-primary text-lg font-medium">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-lg">{testimonial.name}</p>
              {(testimonial.title || testimonial.company) && (
                <p className="text-muted-foreground">
                  {testimonial.title}
                  {testimonial.title && testimonial.company && " at "}
                  {testimonial.company}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {testimonials.length > 1 && (
        <>
          <Button
            size="icon"
            variant="ghost"
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm"
            onClick={prev}
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm"
            onClick={next}
            aria-label="Next testimonial"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
          
          <div className="flex justify-center gap-2 mt-4">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={cn(
                  "w-2 h-2 rounded-full transition-all",
                  i === currentIndex ? "bg-primary w-6" : "bg-primary/30"
                )}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TestimonialGrid({ testimonials, className }: { testimonials: Testimonial[]; className?: string }) {
  return (
    <div className={cn("grid gap-4 md:grid-cols-2 overflow-hidden", className)}>
      {testimonials.map(testimonial => {
        const quote = testimonial.shortQuote || testimonial.fullQuote;
        const initials = testimonial.name.split(" ").map(n => n[0]).join("").slice(0, 2);
        
        return (
          <div 
            key={testimonial.id} 
            className="p-5 sm:p-6 rounded-lg bg-muted/50 hover-elevate overflow-hidden min-w-0"
            data-testid={`testimonial-grid-${testimonial.id}`}
          >
            <blockquote className="text-sm text-foreground mb-4 break-words">
              "{quote.length > 150 ? quote.slice(0, 150) + "..." : quote}"
            </blockquote>
            <div className="flex items-center gap-3 min-w-0">
              <Avatar className="h-10 w-10 flex-shrink-0">
                <AvatarImage src={optimizedImageUrl(testimonial.profilePicture, 80) || undefined} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">{testimonial.name}</p>
                {testimonial.company && (
                  <p className="text-xs text-muted-foreground truncate">{testimonial.company}</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TestimonialFeatured({ testimonials, className }: { testimonials: Testimonial[]; className?: string }) {
  const featured = testimonials[0];
  const others = testimonials.slice(1, 4);
  
  if (!featured) return null;
  
  const quote = featured.shortQuote || featured.fullQuote;
  const initials = featured.name.split(" ").map(n => n[0]).join("").slice(0, 2);

  return (
    <div className={cn("space-y-6", className)}>
      <Card className="bg-primary/5 border-primary/20 overflow-hidden" data-testid="testimonial-featured">
        <CardContent className="p-5 sm:p-8">
          <div className="flex flex-col md:flex-row gap-6 min-w-0">
            <div className="flex-shrink-0">
              <Avatar className="h-20 w-20 border-4 border-primary/20">
                <AvatarImage src={optimizedImageUrl(featured.profilePicture, 160) || undefined} />
                <AvatarFallback className="text-2xl">{initials}</AvatarFallback>
              </Avatar>
            </div>
            <div className="flex-1 min-w-0">
              <Quote className="h-8 w-8 text-primary/30 mb-4" />
              <blockquote className="text-lg md:text-xl text-foreground mb-4 break-words">
                "{quote}"
              </blockquote>
              <div className="min-w-0">
                <p className="font-semibold text-lg truncate">{featured.name}</p>
                {(featured.title || featured.company) && (
                  <p className="text-muted-foreground truncate">
                    {featured.title}
                    {featured.title && featured.company && " at "}
                    {featured.company}
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {others.length > 0 && (
        <div className="grid gap-4 md:grid-cols-3">
          {others.map(t => (
            <TestimonialCard key={t.id} testimonial={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function TestimonialMinimal({ testimonials, className }: { testimonials: Testimonial[]; className?: string }) {
  return (
    <div className={cn("space-y-4", className)}>
      {testimonials.map(testimonial => {
        const quote = testimonial.shortQuote || testimonial.fullQuote;
        
        return (
          <div 
            key={testimonial.id} 
            className="flex items-start gap-3"
            data-testid={`testimonial-minimal-${testimonial.id}`}
          >
            <Quote className="h-5 w-5 text-primary/40 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-muted-foreground italic">
                "{quote.length > 100 ? quote.slice(0, 100) + "..." : quote}"
              </p>
              <p className="text-xs font-medium mt-1">
                {testimonial.name}
                {testimonial.company && <span className="text-muted-foreground"> at {testimonial.company}</span>}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TestimonialHero({ testimonials, className }: { testimonials: Testimonial[]; className?: string }) {
  const testimonial = testimonials[0];
  if (!testimonial) return null;
  
  const quote = testimonial.shortQuote || testimonial.fullQuote;
  const initials = testimonial.name.split(" ").map(n => n[0]).join("").slice(0, 2);

  return (
    <div 
      className={cn(
        "relative py-16 px-8 rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent",
        className
      )}
      data-testid="testimonial-hero"
    >
      <div className="max-w-4xl mx-auto text-center min-w-0">
        <Quote className="h-12 w-12 text-primary/20 mx-auto mb-6" />
        <blockquote className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-medium text-foreground mb-8 leading-relaxed break-words">
          "{quote}"
        </blockquote>
        <div className="flex items-center justify-center gap-4">
          <Avatar className="h-14 w-14 border-2 border-primary/20">
            <AvatarImage src={testimonial.profilePicture || undefined} />
            <AvatarFallback className="text-lg">{initials}</AvatarFallback>
          </Avatar>
          <div className="text-left">
            <p className="font-semibold text-lg">{testimonial.name}</p>
            {(testimonial.title || testimonial.company) && (
              <p className="text-muted-foreground">
                {testimonial.title}
                {testimonial.title && testimonial.company && " at "}
                {testimonial.company}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {testimonials.length > 1 && (
        <div className="flex justify-center gap-4 mt-8">
          {testimonials.slice(1, 4).map(t => {
            const i = t.name.split(" ").map(n => n[0]).join("").slice(0, 2);
            return (
              <Avatar key={t.id} className="h-10 w-10 border-2 border-background">
                <AvatarImage src={t.profilePicture || undefined} />
                <AvatarFallback>{i}</AvatarFallback>
              </Avatar>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { TestimonialCard, TestimonialCarousel, TestimonialGrid, TestimonialFeatured, TestimonialMinimal, TestimonialHero };
